/**
 * This installation's identity.
 *
 * Registers once with the server and keeps the credential it is issued, so a
 * donor can come back and see their own donations without ever creating an
 * account. The id names the installation; the secret is what proves it, and
 * only the secret is checked — a client that claims someone else's id without
 * their secret gets nothing.
 *
 * Nothing here touches device hardware. IMEI, MAC and advertising identifiers
 * are restricted on both platforms, survive an uninstall, and follow a person
 * across unrelated apps; a random per-install value does none of that.
 *
 * The cost, stated plainly rather than hidden: clearing storage or moving to a
 * new phone loses the history, because there is no account to restore it from.
 */

import { Platform } from 'react-native';

import { api } from '../api/client';
import { storage } from '../storage/storage';

const PLATFORM = Platform.OS;
const ID_KEY = 'mfa.installation.id';
const SECRET_KEY = 'mfa.installation.secret';

export interface InstallationCredential {
  installationId: string;
  secret: string;
}

let cached: InstallationCredential | null = null;
/** Collapses concurrent registrations, so a cold start makes one, not five. */
let inFlight: Promise<InstallationCredential | null> | null = null;

async function read(): Promise<InstallationCredential | null> {
  try {
    const [installationId, secret] = await Promise.all([
      storage.getItem(ID_KEY),
      storage.getItem(SECRET_KEY),
    ]);
    // Half a credential is no credential — a partial write means re-register.
    return installationId && secret ? { installationId, secret } : null;
  } catch {
    return null;
  }
}

async function write(credential: InstallationCredential): Promise<void> {
  try {
    await storage.setItem(ID_KEY, credential.installationId);
    await storage.setItem(SECRET_KEY, credential.secret);
  } catch {
    // Storage refused — a private window, or site data blocked. The donation
    // still works; it simply will not be remembered on this device.
  }
}

/**
 * The stored credential, registering one if this installation has none.
 *
 * Returns null when registration fails. Callers treat that as "not identified"
 * and carry on: being unable to register must never stop someone donating.
 */
export async function ensureInstallation(): Promise<InstallationCredential | null> {
  if (cached) return cached;
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const stored = await read();
      if (stored) {
        cached = stored;
        return stored;
      }
      const issued = await api.post<InstallationCredential>('installations/register', {
        platform: PLATFORM,
      });
      await write(issued);
      cached = issued;
      return issued;
    } catch {
      return null;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

/** Auth headers for a request that should be attributed to this installation. */
export async function installationHeaders(): Promise<Record<string, string>> {
  const credential = await ensureInstallation();
  if (!credential) return {};
  return {
    'X-Installation-Id': credential.installationId,
    'X-Installation-Secret': credential.secret,
  };
}

/** Forget the credential, e.g. "forget this device" in Settings. */
export async function forgetInstallation(): Promise<void> {
  cached = null;
  try {
    await storage.removeItem(ID_KEY);
    await storage.removeItem(SECRET_KEY);
  } catch {
    /* Nothing to do if storage will not co-operate. */
  }
}

/** Test seam: drops the in-memory cache without touching storage. */
export function resetInstallationCache(): void {
  cached = null;
  inFlight = null;
}
